"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TechnicalTreatment() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [particles, setParticles] = useState<Array<{
    id: number;
    left: number;
    delay: number;
    duration: number;
    size: number;
  }>>([]);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const generated = Array.from({
      length: 24
    }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 12 + Math.random() * 10,
      size: 1 + Math.random() * 3
    }));
    setParticles(generated);
  }, []);
  const stats = [{
    label: "INTERVENTO",
    value: "PROGRAMMATO"
  }, {
    label: "TECNOLOGIA",
    value: "ROBOTICA"
  }, {
    label: "VERIFICA",
    value: "VIDEO"
  }, {
    label: "REPORT",
    value: "FOTOGRAFICO"
  }];
  return <section data-section-id="228" id="trattamento-tecnico-condotti" ref={sectionRef} className="relative dark bg-background text-foreground overflow-hidden min-h-[90vh] flex items-end">
      {}
      <div className="absolute inset-0 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotti_hvac_acciaio_inox_illuminazione.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotti_hvac_acciaio_inox_illuminazione.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/condotti_hvac_acciaio_inox_illuminazione.webp-wvc-sizes" alt="Dettaglio tecnico di condotti HVAC in acciaio inox con illuminazione industriale" loading="lazy" decoding="async" className="w-full h-full object-cover animate-[slowZoom_25s_ease-in-out_infinite_alternate] brightness-110 contrast-105" />
        {}
        <div className="absolute inset-0" style={{
        background: "linear-gradient(90deg, hsla(222, 47%, 7%, 0.35) 0%, hsla(222, 47%, 7%, 0.50) 30%, hsla(222, 47%, 7%, 0.40) 60%, hsla(222, 47%, 7%, 0.25) 100%)"
      }} />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, hsla(222, 47%, 7%, 0.45) 0%, hsla(222, 47%, 7%, 0.20) 40%, transparent 100%)"
      }} />
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => <span key={p.id} data-index={i} className="absolute rounded-lg bg-primary/30" style={{
        left: `${p.left}%`,
        bottom: `-10px`,
        width: `${p.size}px`,
        height: `${p.size}px`,
        animation: `floatUp ${p.duration}s linear ${p.delay}s infinite`,
        filter: "blur(0.5px)"
      }} />)}
      </div>

      {}
      <div className="absolute top-8 right-4 md:top-12 md:right-12 lg:right-20 pointer-events-none select-none" aria-hidden="true">
        <span className="font-default font-light leading-none text-foreground/[0.06]" style={{
        fontSize: "clamp(180px, 28vw, 380px)"
      }}>
          01
        </span>
      </div>

      {}
      <div className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        <p className="font-mono text-[10px] tracking-[0.3em] text-foreground/50 uppercase whitespace-nowrap" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          BONIFICA &middot; ANALISI &middot; RIQUALIFICAZIONE &middot; CERTIFICAZIONE
        </p>
      </div>

      {}
      <div ref={contentRef} className={`relative z-10 w-full px-6 md:px-12 lg:px-20 xl:pl-[120px] xl:pr-20 pt-32 pb-16 md:pb-20 lg:pb-24 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <span className="block w-12 h-px bg-secondary" />
          <p className="font-mono text-xs tracking-[0.25em] text-secondary uppercase">
            BONIFICA TECNICA &middot; DIVISIONE TECNICA &middot; AERA
          </p>
        </div>

        {}
        <h1 className="font-default font-medium text-secondary leading-[0.85] tracking-tight mb-14 md:mb-16" style={{
        fontSize: "clamp(54px, 12.6vw, 144px)"
      }}>
          <span className="block">BONIFICA</span>
          <span className="block pl-[0.5em]">TECNICA</span>
          <span className="block italic text-primary">IMPIANTI.</span>
        </h1>

        {}
        <p className="font-default text-base md:text-lg leading-relaxed max-w-[560px] mb-10 md:mb-12 text-secondary-foreground">
          Soluzioni specialistiche per condotti HVAC, cappe cucina e reti di estrazione. Attraverso un 
          protocollo di manutenzione sistematica, garantiamo la qualità dell'aria e la riqualificazione 
          igienico-funzionale degli impianti, assicurando massima efficienza e sicurezza operativa.
        </p>

        {}
        <div className="mb-10 md:mb-12 max-w-3xl">
          <div className="flex flex-wrap items-stretch gap-y-4 border-t border-b border-border/40 py-5">
            {stats.map((stat, i) => <div key={stat.label} data-index={i} className={`flex-1 min-w-[140px] px-4 md:px-5 ${i !== 0 ? "md:border-l border-border/40" : ""}`}>
                <p className="font-mono text-[10px] tracking-[0.2em] text-foreground/50 uppercase mb-1.5">
                  {stat.label}
                </p>
                <p className="font-mono text-sm md:text-base font-medium tracking-wider text-accent-foreground">
                  {stat.value}
                </p>
              </div>)}
          </div>
        </div>

        {}
        <div className="flex flex-wrap items-center gap-4">
          <Button asChild size="lg" className="h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-xs tracking-[0.2em] uppercase rounded-lg shadow-lg">
            <Link to="/TechnicalInspectionPage">
              Richiedi sopralluogo
              <ArrowRight className="ml-1 size-4 text-primary-foreground" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-14 px-8 bg-transparent border border-primary/30 text-primary hover:bg-primary/5 hover:text-primary font-default text-xs tracking-[0.2em] uppercase rounded-lg">
            <Link to="/TechnicalDivisionPage">
              <ShieldCheck className="mr-1 size-4 text-primary" />
              Divisione tecnica
            </Link>
          </Button>
        </div>
      </div>

      {}
      <style>{`
        @keyframes slowZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.12); }
        }
        @keyframes floatUp {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          15% {
            opacity: 0.6;
          }
          85% {
            opacity: 0.4;
          }
          100% {
            transform: translateY(-105vh) translateX(20px);
            opacity: 0;
          }
        }
      `}</style>
    </section>;
}